<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">

        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Cashboxes') }}</div>
            <AddNewButton :loading="!allDataLoaded"
                          :disabled="!allDataLoaded || items?.length >= 10"
                          v-show="canModerate"
                          :label="$t('Add')"
                          @click="addNewItem" />
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: 'name', title: $t('Name'),sortable: true},
              {name: 'description', title: $t('Description'), sortable: false},
              {name: 'access', title: $t('Access'), sortable: false}
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.name">{{ item.name }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.description">{{ item.description }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <div v-if="item.users?.length" class="p-badge badge"
                     v-tooltip.top="createTooltipData(item)">{{ item.users.length }}
                </div>
                <div v-else class="p-badge badge">0</div>
              </td>
              <td v-show="actionButtonsCount">
                <div class="actions">
                  <EditButton v-show="canModerate"
                              :disabled="!allDataLoaded || dataTableIsLoading" @click="editItem(item)"/>
                  <DeleteButton v-show="canModerate"
                                @click="confirmItemDelete(item)"
                                :disabled="dataTableIsLoading"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="items && items.length"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <CashboxModal :visible="itemModal"
                      :item="item"
                      :cashboxUsers="employees"
                      @update-items="updateItems"
                      @close="closeItemModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="itemToDeleteData"/>
			</div>

		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import CashboxModal from '@/pages/data_directory/components/CashboxModal'
import formatMixins from "@/mixins/formatMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import httpMixins from "@/mixins/httpMixins";

export default {
  mixins: [ formatMixins, showErrorsMixins, datatableMixins, httpMixins ],
  components: { CashboxModal },
	data() {
		return {
      employees: null,
      fieldsExpand: 'users',
      apiLink: 'cashbox',
      deleteItemUrl: 'cashbox/delete',
      routePath: '/cashboxes',
      sort: {
        column: 'id',
        direction: '-',
      },
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems()
      this.getCashboxUsers()
    },
    createTooltipData(item) {
      if (!item.users?.length) return false

      return item.users
          .map(item => `${item.user?.first_name || ''} ${item.user?.last_name || ''}`.trim())
          .filter(Boolean)
          .join('<br>')
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('cashboxes')
      try {
        // const { status, data, headers } = await httpClient(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.dataTableIsLoading = false
        this.isLoading = false
        if (this._abortors?.cashboxes === ctrl) {
          this._abortors.cashboxes = null
        }
      }
    },
	},
  computed: {
    canModerate() {
      return this.$store.getters.isAdminOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    allDataLoaded() {
      return !!(this.employees)
    },
    itemToDeleteData() {
      if (!this.itemToDelete?.name) return null
      return this.itemToDelete.name
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}
</style>